import { DropzoneProps } from 'react-dropzone'

import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import i18n from 'modules/core/lib/i18n'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import { FileTypeCategory } from 'modules/trainings/components/Files/types'
import { formatFileSize } from 'modules/trainings/components/Files/utils'

export default function Dropper({
  fileTypes,
  description,
  onDrop,
  error,
}: {
  fileTypes: FileTypeCategory[]
  description: string
  onDrop(result: { file: File; type: 'success' } | { error: string; type: 'error' }): void
  error?: boolean
}) {
  return (
    <DropzoneBox
      accept={toDropzoneFormat(fileTypes)}
      description={description}
      error={error}
      iconName="file-upload"
      message={i18n.t('documents.drop_here')}
      multiple={false}
      onDrop={(files) => {
        const result = validateFile(files[0])
        switch (result.type) {
          case 'success': {
            onDrop({ file: result.file, type: 'success' })
            break
          }
          case 'error': {
            onDrop({ error: result.error, type: 'error' })
            break
          }
        }
      }}
    />
  )
}

const FILE_TYPES_BY_CATEGORY: Record<FileTypeCategory, string[]> = {
  document: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  text: ['text/plain', 'text/csv', 'text/markdown'],
  video: ['video/mp4', 'video/webm'], // Only web video formats are supported
}
function toDropzoneFormat(categories: FileTypeCategory[]): NonNullable<DropzoneProps['accept']> {
  return acceptedContentTypes(categories.flatMap((category) => FILE_TYPES_BY_CATEGORY[category]))
}

function fileCategory(file: File): FileTypeCategory {
  const result = file.type.split('/').at(0)

  switch (result) {
    case 'application': {
      return 'document' as const
    }
    case 'image': {
      return 'image' as const
    }
    case 'text': {
      return 'text' as const
    }
    case 'video': {
      return 'video' as const
    }
    default: {
      throw new Error('unknown category')
    }
  }
}

const MAX_VIDEO_SIZE = 8 * 1024 * 1024 * 1024 // 8GB in bytes
const MAX_FILE_SIZE = 200 * 1024 * 1024 // 200MB in bytes

function validateFile(
  file: File | undefined
): { file: File; type: 'success' } | { error: string; type: 'error' } {
  if (!file) throw new Error('No file dropped')

  const categoryMaxSize = maxAllowedSize(fileCategory(file))

  if (file.size > categoryMaxSize) {
    return {
      type: 'error',
      error: i18n.t('trainings.trainings_files_modal.too_large', {
        maxAllowedSize: formatFileSize(categoryMaxSize),
      }),
    }
  }

  return { file, type: 'success' }

  function maxAllowedSize(category: FileTypeCategory) {
    switch (category) {
      case 'video': {
        return MAX_VIDEO_SIZE
      }
      default: {
        return MAX_FILE_SIZE
      }
    }
  }
}
